// src/utils/session.ts

export interface UserData {
  id?: number;
  username?: string;
  email: string;
  role?: string;
  modules?: string[];
}

export interface SessionData {
  token: string;
  user: UserData;
  modules?: string[];
}

export const saveSession = (
  token: string,
  user: UserData,
  modules?: string[],
  remember: boolean = true
): void => {
  const storage = remember ? localStorage : sessionStorage;

  storage.setItem("auth", "true");
  storage.setItem("auth_token", token ?? ""); // may be empty for Clerk users
  storage.setItem("user", JSON.stringify(user));

  if (modules) storage.setItem("user_modules", JSON.stringify(modules));

  window.dispatchEvent(new Event("storage"));
};

export const getSession = (): SessionData | null => {
  const token =
    localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const modulesStr =
    localStorage.getItem("user_modules") ||
    sessionStorage.getItem("user_modules");

  if (!userStr) return null;

  try {
    const user = JSON.parse(userStr);
    const modules = modulesStr ? JSON.parse(modulesStr) : undefined;

    return { token: token ?? "", user, modules };
  } catch (e) {
    console.error("Error parsing session data:", e);
    return null;
  }
};

/**
 * CRA + Clerk hybrid:
 * - UI auth should be based on the "auth" flag you set on login
 * - API auth should come from ClerkTokenProvider (JWT via Authorization header)
 */
export const isAuthenticated = (): boolean => {
  const auth =
    localStorage.getItem("auth") === "true" ||
    sessionStorage.getItem("auth") === "true";
  return !!auth;
};

export const getToken = (): string | null => {
  const t =
    localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
  if (!t) return null;
  if (t.trim().length === 0) return null;
  return t;
};

export const getUser = (): UserData | null => {
  const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch (e) {
    console.error("Error parsing user data:", e);
    return null;
  }
};

export const getUserModules = (): string[] => {
  const modulesStr =
    localStorage.getItem("user_modules") ||
    sessionStorage.getItem("user_modules");
  if (!modulesStr) return [];
  try {
    return JSON.parse(modulesStr);
  } catch (e) {
    console.error("Error parsing modules:", e);
    return [];
  }
};

export const hasModuleAccess = (moduleName: string): boolean => {
  const user = getUser();
  if (user?.role === "admin") return true;
  const modules = getUserModules();
  return modules.includes(moduleName);
};

export const isAdmin = (): boolean => {
  const user = getUser();
  return user?.role === "admin";
};

export const clearSession = (): void => {
  localStorage.removeItem("auth");
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user");
  localStorage.removeItem("user_modules");

  sessionStorage.removeItem("auth");
  sessionStorage.removeItem("auth_token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("user_modules");

  window.dispatchEvent(new Event("storage"));
};

export const setupCrossTabSync = (
  onSessionChange: (isAuth: boolean) => void
): (() => void) => {
  const handleStorageChange = () => {
    onSessionChange(isAuthenticated());
  };

  // Cross-tab storage events
  window.addEventListener("storage", handleStorageChange);
  // Same-tab custom event (we dispatch "storage" manually)
  window.addEventListener("storage", handleStorageChange);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
  };
};
